import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import Image from '@/routing/Image'
import { ChevronLeft, Search } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'
import CinematicAudienceCard from './CinematicAudienceCard'
import type { CinematicAudienceCardProps } from './CinematicAudienceCard'
import type { CompanyProfileData } from './ProviderSection'

interface ViewAllPanelProps {
  provider: CompanyProfileData | null
  audiences: CinematicAudienceCardProps['audience'][]
  open: boolean
  onClose: () => void
  formatRelativeTime: (date: string) => string
  renderActionMenu?: (audience: CinematicAudienceCardProps['audience']) => React.ReactNode
  onCardClick?: (audienceId: string) => void
  onBookmarkToggle?: (id: string, nextBookmarked: boolean) => Promise<void>
}

export default function ViewAllPanel({
  provider,
  audiences,
  open,
  onClose,
  formatRelativeTime,
  onCardClick,
  renderActionMenu,
  onBookmarkToggle,
}: ViewAllPanelProps) {
  const { t } = useTranslation('app')
  const [query, setQuery] = useState('')

  const filtered = useMemo(() => {
    if (!query.trim()) return audiences
    const q = query.toLowerCase()
    return audiences.filter((a) => {
      const haystack = [
        a.name,
        a.aiDescription,
        a.description,
        a.audienceType,
        ...(a.contentLabels?.map((l) => l.name) || a.aiTags || []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return haystack.includes(q)
    })
  }, [audiences, query])

  const badgeEl =
    provider?.badge === 'verified' ? (
      <Badge variant="positive" className="text-[11px] uppercase tracking-wider">
        Verified
      </Badge>
    ) : provider?.badge === 'partner' ? (
      <Badge variant="info" className="text-[11px] uppercase tracking-wider">
        Partner
      </Badge>
    ) : null

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose() }}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[1200px] p-0 overflow-y-auto"
      >
        {provider && (
          <>
            {/* Sticky header */}
            <div className="sticky top-0 z-10 bg-background border-b border-border px-8 pt-8 pb-5">
              <Button
                variant="ghost"
                size="sm"
                className="mb-5 -ml-2 text-muted-foreground"
                onClick={onClose}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                {t('audiences.backToAudiences')}
              </Button>

              <div className="flex items-center gap-3.5">
                <div
                  className="w-[52px] h-[52px] rounded-xl overflow-hidden border border-border flex items-center justify-center shrink-0"
                  style={
                    provider.brandColors?.primary
                      ? { backgroundColor: `hsl(${provider.brandColors.primary})` }
                      : undefined
                  }
                >
                  {provider.logoUrl ? (
                    <Image
                      src={provider.logoUrl}
                      alt={provider.name}
                      width={32}
                      height={32}
                      className="object-contain"
                      unoptimized
                    />
                  ) : (
                    <span className="text-lg font-bold text-primary-foreground">
                      {provider.name.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-2.5">
                    <Heading level={2}>{provider.name}</Heading>
                    {badgeEl}
                  </div>
                  <div className="flex items-center gap-2.5 mt-0.5">
                    {provider.description && (
                      <Text size="sm" color="muted">
                        {provider.description}
                      </Text>
                    )}
                    {provider.description && (
                      <span className="w-[3px] h-[3px] rounded-full bg-muted-foreground/40" />
                    )}
                    <Text size="sm" color="muted">
                      {audiences.length} audience{audiences.length !== 1 ? 's' : ''}
                    </Text>
                  </div>
                </div>
              </div>

              {/* Search */}
              <div className="relative mt-5">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                  className="pl-11 rounded-xl"
                  placeholder={t('audienceActions.filterPlaceholder')}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
            </div>

            {/* Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-7">
              {filtered.length > 0 ? (
                filtered.map((audience) => (
                  <CinematicAudienceCard
                    key={audience.id}
                    audience={audience}
                    provider={{ name: provider.name, logoUrl: provider.logoUrl }}
                    formatRelativeTime={formatRelativeTime}
                    onClick={onCardClick ? () => onCardClick(audience.id) : undefined}
                    actionMenu={renderActionMenu?.(audience)}
                    onBookmarkToggle={onBookmarkToggle}
                  />
                ))
              ) : (
                <div className="col-span-full text-center py-16 text-muted-foreground">
                  {t('audienceActions.noFilterMatch')}
                </div>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}
